import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { createSuggestionRenderer } from "./TextExpansionSuggestion";
import { CLINICAL_RICH_TEXT_PARSE_OPTIONS } from "../../../utils/clinicalRichText";

export interface TextExpansionStorage {
  abbreviationMap: Map<string, string>;
}

/**
 * Converts literal `??` tokens in abbreviation expansion HTML into
 * `<span data-variable>` wrappers so the VariableChip TipTap node
 * renders them as interactive free-text input fields.
 */
function convertPlaceholderTokens(html: string): string {
  if (!html || !html.includes("??")) return html;
  let counter = 1;
  return html.replace(/\?\?/g, () => {
    const name = `field_${counter}`;
    counter += 1;
    return `<span data-variable="${name}">{{${name}}}</span>`;
  });
}

const TextExpansion = Extension.create<
  Record<string, never>,
  TextExpansionStorage
>({
  name: "textExpansion",

  addStorage() {
    return {
      abbreviationMap: new Map<string, string>(),
    };
  },

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey("textExpansion"),
        props: {
          handleKeyDown(view, event) {
            if (event.key === "Tab" && !event.shiftKey) {
              const { state } = view;
              const { $from } = state.selection;

              const parentNode = $from.node(-1);
              if (parentNode && parentNode.type.name === "listItem") {
                return false;
              }

              const parentStart = $from.start();
              const textBefore = state.doc.textBetween(
                parentStart,
                $from.pos,
                "\n",
                "\0"
              );

              const match = textBefore.match(
                /(^|[\s])(`{0,1}[A-Za-z0-9]{1,50})$/
              );

              if (match) {
                const shortcode = match[2];
                const map: Map<string, string> =
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (editor.storage as any).textExpansion.abbreviationMap;
                const expansion = map.get(shortcode.toLowerCase());

                if (expansion) {
                  const shortcodeLength = shortcode.length;
                  const from = $from.pos - shortcodeLength;
                  const to = $from.pos;

                  event.preventDefault();
                  editor
                    .chain()
                    .insertContentAt(
                      { from, to },
                      convertPlaceholderTokens(expansion),
                      {
                        parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
                      }
                    )
                    .run();
                  return true;
                }
              }

              event.preventDefault();
              view.dispatch(state.tr.insertText("    "));
              return true;
            }

            if (event.key === "Tab" && event.shiftKey) {
              const { state } = view;
              const { $from } = state.selection;
              const parentNode = $from.node(-1);
              if (parentNode && parentNode.type.name === "listItem") {
                return false;
              }
            }

            return false;
          },
        },
      }),
      Suggestion({
        editor,
        char: "`",
        pluginKey: new PluginKey("textExpansionSuggestion"),
        items: ({ query }: { query: string }) => {
          const map: Map<string, string> =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor.storage as any).textExpansion.abbreviationMap;
          const results: { shortcode: string; expansion: string }[] = [];
          const q = query.toLowerCase();

          map.forEach((expansion, key) => {
            const shortcode = key.startsWith("`") ? key : `\`${key}`;
            if (shortcode.toLowerCase().includes(`\`${q}`)) {
              results.push({ shortcode, expansion });
            }
          });

          return results.slice(0, 10);
        },
        command: ({
          editor: ed,
          range,
          props,
        }: {
          editor: typeof editor;
          range: { from: number; to: number };
          props: { shortcode: string; expansion: string };
        }) => {
          ed.chain()
            .insertContentAt(range, convertPlaceholderTokens(props.expansion), {
              parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
            })
            .run();
        },
        render: createSuggestionRenderer,
      }),
    ];
  },
});

export default TextExpansion;
